<template>
  <div class="container">
    <Head :title="`Venta #${order.number_order ?? order.id} ${order.order_state == 'cancel' ? 'PEDIDO ANULADO' : ''}`" />
    <div class="flex-col items-center p-5 text-center bg-white rounded-lg shadow-lg">
      <div>
        <i class="fa fa-check text-green-500 text-2xl"></i>
        <h1 class="mb-8 text-2xl font-bold">Venta #{{ order.number_order }} creado correctamente</h1>
        <!-- Modal de Cobro (Overlay sobre el POS) -->
        <div v-if="order.order_state == 'request'" class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style="background-color: rgba(0, 0, 0, 0.65); backdrop-filter: blur(2px);">
          <payment-box
            :order="order"
            :payment_methods="payment_methods"
            :change="change"
            :cop_change="cop_change"
            :cancellable="true"
            @cancel="cancelOrder"
          />
        </div>
        <div v-else class="my-6">
          <div class="flex flex-wrap justify-center items-center gap-2 mb-4">
            <span class="text-xs font-bold text-gray-500 uppercase w-full block">Impresión Térmica:</span>
            <button class="px-3.5 py-2 text-xs sm:text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow flex items-center gap-1.5 transition-colors cursor-pointer" type="button" @click="print('usd')">
              <i class="fa fa-print"></i> Ticket USD
            </button>
            <button class="px-3.5 py-2 text-xs sm:text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow flex items-center gap-1.5 transition-colors cursor-pointer" type="button" @click="print('ves')">
              <i class="fa fa-print"></i> Ticket Bs
            </button>
            <button class="px-3.5 py-2 text-xs sm:text-sm font-bold text-white rounded-lg shadow flex items-center gap-1.5 transition-colors cursor-pointer" style="background-color: #00b074;" type="button" @click="print('cop')">
              <i class="fa fa-print"></i> Ticket COP
            </button>

            <div class="h-6 w-px bg-gray-300 mx-1 hidden sm:block"></div>

            <button class="px-3.5 py-2 text-xs sm:text-sm font-bold text-white bg-purple-700 hover:bg-purple-800 rounded-lg shadow flex items-center gap-1.5 transition-colors cursor-pointer" type="button" @click="printNota(effectiveWhatsAppCurrency)">
              <i class="fa fa-file-pdf"></i> Nota de Entrega PDF ({{ effectiveWhatsAppCurrency.toUpperCase() }})
            </button>
          </div>

          <!-- Envío por WhatsApp con Selector de Moneda -->
          <div class="flex flex-col sm:flex-row justify-center items-center gap-2.5 mb-6 bg-green-50 p-3 sm:p-4 rounded-xl border border-green-200 shadow-sm max-w-2xl mx-auto">
            <div class="flex items-center gap-1 text-xs font-bold text-green-950">
              <i class="fab fa-whatsapp text-green-600 text-lg mr-1"></i>
              <span>Moneda WhatsApp:</span>
              <select v-model="whatsappCurrency" class="border border-green-300 rounded px-2 py-1 text-xs font-black bg-white text-gray-800 outline-none">
                <option value="auto">Auto (según cobro: {{ detectedCurrency.toUpperCase() }})</option>
                <option value="mixto">Mixto / Multi-moneda</option>
                <option value="cop">Pesos (COP)</option>
                <option value="bs">Bolívares (Bs)</option>
                <option value="usd">Dólares ($)</option>
              </select>
            </div>
            <div class="flex items-center gap-2 w-full sm:w-auto">
              <input v-model="numeroWhatsapp" type="text" placeholder="WhatsApp (ej: 0412... o 300...)" class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 sm:w-56" />
              <button class="px-4 py-2 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow flex items-center gap-1.5 cursor-pointer transition-colors" type="button" @click="sendWhatsApp()">
                <i class="fab fa-whatsapp"></i> Enviar
              </button>
            </div>
          </div>

          <div class="mt-4">
            <a class="px-8 py-3.5 text-base font-bold text-blue-900 bg-yellow-400 hover:bg-yellow-300 rounded-xl shadow-lg transition-all transform hover:scale-105 inline-flex items-center gap-2 cursor-pointer" :href="`/orders/finish/${order.id}`">
              <i class="fa fa-plus-circle"></i> Nueva venta [F9]
            </a>
          </div>
        </div>
      </div>

      <!-- Detalle de Pago y Monedas Registradas -->
      <div v-if="order.pays && order.pays.length" class="w-full my-6 text-left">
        <div class="bg-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-base font-bold text-gray-800 flex items-center">
              <i class="fa fa-money-bill-wave text-emerald-600 mr-2 text-lg"></i>
              Detalle de Pago / Monedas de la Venta
            </h3>
            <span class="text-xs font-semibold px-2.5 py-1 bg-green-100 text-green-800 rounded-full">
              {{ order.pays.length }} {{ order.pays.length === 1 ? 'pago registrado' : 'pagos registrados' }}
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div v-for="pay in order.pays" :key="pay.id" class="p-3 rounded-lg border flex flex-col justify-between"
              :class="{
                'bg-emerald-50 border-emerald-300': getPayDetails(pay).type === 'usd',
                'bg-blue-50 border-blue-300': getPayDetails(pay).type === 'bs',
                'bg-purple-50 border-purple-300': getPayDetails(pay).type === 'cop'
              }">
              <div class="flex items-center justify-between mb-1.5">
                <span class="font-bold text-sm text-gray-900">{{ pay.method }}</span>
                <span v-if="getPayDetails(pay).type === 'cop'" class="px-2 py-0.5 text-xs font-black rounded bg-purple-200 text-purple-900">COP</span>
                <span v-else-if="getPayDetails(pay).type === 'bs'" class="px-2 py-0.5 text-xs font-black rounded bg-blue-200 text-blue-900">Bs</span>
                <span v-else class="px-2 py-0.5 text-xs font-black rounded bg-emerald-200 text-emerald-900">USD ($)</span>
              </div>
              <div class="text-lg font-black text-gray-900">
                <template v-if="getPayDetails(pay).type === 'cop'">
                  COP {{ Number(pay.amount_cop).toLocaleString('es-CO') }}
                  <span class="text-xs text-gray-600 block font-normal">
                    (Equiv. ${{ Number(pay.amount).toFixed(2) }} USD)
                    <span v-if="getPayDetails(pay).rateText" class="text-purple-700 font-bold ml-1">• Tasa: {{ getPayDetails(pay).rateText }}</span>
                  </span>
                </template>
                <template v-else-if="getPayDetails(pay).type === 'bs'">
                  Bs. {{ Number(pay.amount_bs).toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}
                  <span class="text-xs text-gray-600 block font-normal">
                    (Equiv. ${{ Number(pay.amount).toFixed(2) }} USD)
                    <span v-if="getPayDetails(pay).rateText" class="text-blue-700 font-bold ml-1">• Tasa: {{ getPayDetails(pay).rateText }}</span>
                  </span>
                </template>
                <template v-else>
                  ${{ Number(pay.amount).toFixed(2) }} USD
                  <span v-if="getPayDetails(pay).rateText" class="text-xs text-emerald-700 block font-bold">
                    Tasa recibida: {{ getPayDetails(pay).rateText }}
                  </span>
                </template>
              </div>
              <div v-if="pay.reference" class="mt-2 text-xs text-gray-700 bg-white bg-opacity-70 px-2 py-1 rounded border border-gray-200">
                <span class="font-semibold text-gray-900">Ref:</span> {{ pay.reference }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalle de Vuelto / Cambio Entregado al Cliente -->
      <div v-if="vueltoInfo && vueltoInfo.hasVuelto" class="w-full my-4 text-left">
        <div class="bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 border-2 border-emerald-400 rounded-xl p-5 shadow-sm">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-emerald-200 pb-3 mb-3">
            <div>
              <span class="text-xs font-black text-emerald-900 uppercase tracking-wider flex items-center">
                <i class="fa fa-hand-holding-usd text-emerald-600 mr-2 text-xl"></i>
                Vuelto Entregado al Cliente
              </span>
              <p class="text-xs text-gray-600 mt-0.5">
                Dinero devuelto físicamente al cliente al momento de cobrar la venta:
              </p>
            </div>
            <div class="text-right">
              <span class="px-3 py-1 bg-emerald-200 text-emerald-950 text-xs font-black rounded-full shadow-sm uppercase inline-flex items-center">
                <i class="fa fa-check-circle mr-1"></i> Descontado de Caja
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div v-if="vueltoInfo.cop > 0" class="bg-white p-3.5 rounded-lg border-2 border-purple-300 shadow-sm flex flex-col justify-between">
              <span class="text-xs font-bold text-purple-900 block mb-1">Vuelto en Pesos Efectivo (COP):</span>
              <span class="text-2xl font-black text-purple-950">COP {{ Number(vueltoInfo.cop).toLocaleString('es-CO') }}</span>
              <span class="text-[11px] text-gray-500 font-semibold mt-1">Billetes físicos entregados</span>
            </div>
            <div v-if="vueltoInfo.usd > 0" class="bg-white p-3.5 rounded-lg border-2 border-emerald-300 shadow-sm flex flex-col justify-between">
              <span class="text-xs font-bold text-emerald-900 block mb-1">Vuelto en Dólares Efectivo ($ USD):</span>
              <span class="text-2xl font-black text-emerald-950">${{ Number(vueltoInfo.usd).toFixed(2) }} USD</span>
              <span class="text-[11px] text-gray-500 font-semibold mt-1">Billetes físicos entregados</span>
            </div>
            <div v-if="vueltoInfo.bs > 0" class="bg-white p-3.5 rounded-lg border-2 border-sky-300 shadow-sm flex flex-col justify-between">
              <span class="text-xs font-bold text-sky-900 block mb-1">Vuelto en Bolívares (Bs):</span>
              <span class="text-2xl font-black text-sky-950">Bs. {{ Number(vueltoInfo.bs).toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span>
              <span class="text-[11px] text-gray-500 font-semibold mt-1">Efectivo entregado</span>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full">
        <div class="w-full px-0 mx-0 my-5 overflow-x-auto" style="max-width:100vw;">
          <table class="w-full min-w-[900px] border border-gray-300 rounded-lg shadow-sm bg-white">
            <thead class="bg-blue-600 text-white sticky top-0 z-10">
              <tr>
                <th class="py-1 px-2 text-left font-semibold text-sm">Producto</th>
                <th class="py-1 px-2 text-left font-semibold text-sm">Descripción</th>
                <th class="py-1 px-2 text-center font-semibold text-sm">Cantidad</th>
                <th class="py-1 px-2 text-right font-semibold text-sm">Precio</th>
                <th class="py-1 px-2 text-right font-semibold text-sm">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, key) in order.products" :key="key" :class="[key % 2 === 0 ? 'bg-gray-50' : 'bg-white', product.dispatch ? 'bg-blue-200' : '', 'hover:bg-blue-100 transition']">
                <td class="px-2 border-t border-gray-200 text-sm">{{ product.product.name }}</td>
                <td class="px-2 border-t border-gray-200 text-sm">{{ product.product.description }}</td>
                <td class="px-2 border-t border-gray-200 text-center text-sm">{{ product.quantity }}</td>
                <td class="px-2 border-t border-gray-200 text-right text-sm">
                  <span class="block">${{ Number(product.price || 0).toFixed(2) }}</span>
                  <span class="block text-xs text-gray-500">BsD. {{ (Number(product.price || 0) * (change || 0)).toFixed(2) }}</span>
                  <span class="block text-xs text-gray-500">Cop. {{ (product.product?.price_cop ? Number(product.product.price_cop) : (Number(product.price || 0) * (cop_change || 1))).toFixed(2) }}</span>
                </td>
                <td class="px-2 border-t border-gray-200 text-right text-sm">
                  <span class="block font-semibold">${{ (Number(product.price || 0) * Number(product.quantity || 1)).toFixed(2) }}</span>
                  <span class="block text-xs text-gray-500">BsD. {{ (Number(product.price || 0) * Number(product.quantity || 1) * (change || 0)).toFixed(2) }}</span>
                  <span class="block text-xs text-gray-500">Cop. {{ ((product.product?.price_cop ? Number(product.product.price_cop) : (Number(product.price || 0) * (cop_change || 1))) * Number(product.quantity || 1)).toFixed(2) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-100">
              <tr v-if="order.discount > 0">
                <td colspan="3"></td>
                <td class="px-2 text-right border-t font-semibold text-sm text-red-600">Descuento</td>
                <td class="px-2 text-right border-t font-semibold text-sm text-red-600">
                  <span class="block">${{ order.discount }}</span>
                  <span class="block text-xs text-red-500">BsD. {{ (Number(order.discount) * (change || 0)).toFixed(2) }}</span>
                  <span class="block text-xs text-red-500">Cop. {{ (order.total > 0 ? (Number(order.discount) * (Number(order.total_cop) || (Number(order.total) * (cop_change || 1))) / Number(order.total)) : 0).toFixed(2) }}</span>
                </td>
              </tr>
              <tr>
                <td colspan="3"></td>
                <td class="px-2 text-right border-t font-bold text-sm">Total</td>
                <td class="px-2 text-right border-t font-bold text-sm">
                  <span class="block">${{ order.total }}</span>
                  <span class="block text-xs text-gray-700">BsD. {{ (Number(order.total || 0) * (change || 0)).toFixed(2) }}</span>
                  <span class="block text-xs text-gray-700">Cop. {{ order.total_cop || (Number(order.total || 0) * (cop_change || 1)) }}</span>
                </td>
              </tr>
              <tr>
                <td colspan="3"></td>
                <td class="px-2 text-right border-t font-semibold text-sm">Deuda</td>
                <td class="px-2 text-right border-t font-semibold text-sm">
                  <span class="block">${{ order.total - order.balance }}</span>
                  <span class="block text-xs text-gray-700">BsD. {{ ((order.total - order.balance) * change).toFixed(2) }}</span>
                </td>
              </tr>
              <tr>
                <td colspan="3"></td>
                <td class="px-2 text-right border-t font-semibold text-sm">Balance</td>
                <td class="px-2 text-right border-t font-semibold text-sm">
                  <span class="block">${{ order.balance }}</span>
                  <span class="block text-xs text-gray-700">BsD. {{ (order.balance * change).toFixed(2) }}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div class="flex"></div>
    </div>
  </div>
</template>
<script>
import LayoutFullVue from '../../Shared/LayoutFull.vue'
import TextInput from '../../Shared/TextInput.vue'
import { Head } from '@inertiajs/inertia-vue3'
import PaymentBox from '../../Shared/PaymentBox.vue'

export default {
  layout: LayoutFullVue,
  components: {
    TextInput,
    Head,
    PaymentBox,
  },
  props: {
    order: Object,
    role: Object,
    payment_methods: Object,
    change: Number,
    cop_change: Number,
  },
  data() {
    return {
      show: true,
      whatsappCurrency: 'auto',
      numeroWhatsapp: (this.order.client && this.order.client.phone && this.order.client.phone !== '0414409888391') ? this.order.client.phone : '',
      form: this.$inertia.form({
        method: 'efectivo',
        amount: this.order.total - this.order.balance,
        order_id: this.order.id,
        quick: true,
      }),
    }
  },
  computed: {
    detectedCurrency() {
      if (this.order && this.order.pays && this.order.pays.length > 0) {
        let hasCop = false
        let hasBs = false
        let hasUsd = false

        this.order.pays.forEach(p => {
          const m = (p.method || '').toLowerCase()
          const amtCop = parseFloat(p.amount_cop) || 0
          const amtBs = parseFloat(p.amount_bs) || 0
          const amtUsd = parseFloat(p.amount) || 0

          const isUsdMethod = m.includes('dolar') || m.includes('dollar') || m.includes('usd') || m.includes('zelle')
          const isCopMethod = m.includes('peso') || m.includes('cop') || m.includes('bancolombia') || m.includes('nequi') || m.includes('daviplata')
          const isBsMethod = m.includes('bs') || m.includes('bolivar') || m.includes('pago móvil') || m.includes('pago movil') || m.includes('punto')

          if (isUsdMethod) {
            hasUsd = true
          } else if (isCopMethod || (amtCop > 0 && !isBsMethod)) {
            hasCop = true
          } else if (isBsMethod || (amtBs > 0 && !isCopMethod)) {
            hasBs = true
          } else if (amtUsd > 0) {
            hasUsd = true
          }
        })

        const count = (hasCop ? 1 : 0) + (hasBs ? 1 : 0) + (hasUsd ? 1 : 0)
        if (count > 1) return 'mixto'
        if (hasCop) return 'cop'
        if (hasBs) return 'bs'
        if (hasUsd) return 'usd'
      }

      if (this.order && parseFloat(this.order.total_cop) > 0) {
        return 'cop'
      }
      return 'usd'
    },

    effectiveWhatsAppCurrency() {
      if (this.whatsappCurrency === 'auto') {
        return this.detectedCurrency
      }
      return this.whatsappCurrency
    },

    vueltoInfo() {
      if (!this.order) return null

      // 1. Si existe retiro por vuelto registrado en cash_transactions
      if (this.order.cash_transactions && this.order.cash_transactions.length) {
        const tx = this.order.cash_transactions.find(t => t.type === 'withdrawal' && (t.description || '').toLowerCase().includes('vuelto'))
        if (tx && ((parseFloat(tx.amount_cop) || 0) > 0 || (parseFloat(tx.amount) || 0) > 0 || (parseFloat(tx.amount_bs) || 0) > 0)) {
          return {
            hasVuelto: true,
            cop: parseFloat(tx.amount_cop) || 0,
            usd: parseFloat(tx.amount) || 0,
            bs: parseFloat(tx.amount_bs) || 0,
            description: tx.description
          }
        }
      }

      // 2. Si no viene en cash_transactions pero el total pagado supera el total
      if (this.order.pays && this.order.pays.length) {
        const totalPaidUsd = this.order.pays.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)
        const totalOrderUsd = parseFloat(this.order.total) || 0
        const diffUsd = totalPaidUsd - totalOrderUsd

        if (diffUsd > 0.01) {
          const effChange = this.change > 0 ? this.change : 1
          const effCopRate = (this.order.total > 0 && this.order.total_cop > 0)
            ? (this.order.total_cop / this.order.total)
            : (effChange * (this.cop_change || 1))

          return {
            hasVuelto: true,
            cop: Math.round(diffUsd * effCopRate / 100) * 100,
            usd: diffUsd,
            bs: diffUsd * effChange,
            description: null
          }
        }
      }

      return null
    }
  },
  methods: {
    getPayDetails(pay) {
      const m = (pay.method || '').toLowerCase()
      const amtCop = parseFloat(pay.amount_cop) || 0
      const amtBs = parseFloat(pay.amount_bs) || 0
      const amtUsd = parseFloat(pay.amount) || 0

      const isUsdMethod = m.includes('dolar') || m.includes('dollar') || m.includes('usd') || m.includes('zelle')
      const isCopMethod = m.includes('peso') || m.includes('cop') || m.includes('bancolombia') || m.includes('nequi') || m.includes('daviplata')
      const isBsMethod = m.includes('bs') || m.includes('bolivar') || m.includes('pago móvil') || m.includes('pago movil') || m.includes('punto')

      let type = 'usd'
      if (isUsdMethod) {
        type = 'usd'
      } else if (isCopMethod || (amtCop > 0 && !isBsMethod)) {
        type = 'cop'
      } else if (isBsMethod || (amtBs > 0 && !isCopMethod)) {
        type = 'bs'
      }

      let rateText = null
      if (type === 'cop' && amtUsd > 0 && amtCop > 0) {
        rateText = `${Math.round(amtCop / amtUsd).toLocaleString('es-CO')} COP/$`
      } else if (type === 'bs' && amtUsd > 0 && amtBs > 0) {
        rateText = `${(amtBs / amtUsd).toFixed(2)} Bs/$`
      } else if (type === 'usd' && amtCop > 0 && amtUsd > 0 && amtCop !== amtUsd) {
        rateText = `${Math.round(amtCop / amtUsd).toLocaleString('es-CO')} COP/$`
      }

      return { type, amtCop, amtBs, amtUsd, rateText }
    },

    printNota(curr) {
      const c = curr || this.effectiveWhatsAppCurrency || this.detectedCurrency
      window.open(`/orders/${this.order.id}/report-storer/${c}`, `Nota #${this.order.number_order}`, 'width=900,height=800')
    },

    sendWhatsApp() {
      let rawNum = (this.numeroWhatsapp || '').replace(/[^0-9]/g, '');
      if (!rawNum) {
        alert('Por favor ingrese un número de teléfono para enviar el mensaje.');
        return;
      }

      let numero = rawNum;
      if (rawNum.startsWith('04') && rawNum.length === 11) {
        numero = '58' + rawNum.substring(1);
      } else if ((rawNum.startsWith('412') || rawNum.startsWith('414') || rawNum.startsWith('424') || rawNum.startsWith('416') || rawNum.startsWith('426')) && rawNum.length === 10) {
        numero = '58' + rawNum;
      } else if (rawNum.startsWith('3') && rawNum.length === 10) {
        numero = '57' + rawNum;
      }

      const curr = this.effectiveWhatsAppCurrency
      const mensaje = this.buildWhatsAppMessage(curr);
      const url = `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
    },

    buildWhatsAppMessage(currency) {
      let mensaje = `*Nota de Entrega / Factura #${this.order.number_order ?? this.order.id}*\n`
      if (this.order.client && this.order.client.name && this.order.client.name !== 'Cliente por defecto') {
        mensaje += `*Cliente:* ${this.order.client.name}\n`
      }
      mensaje += `\n*Detalle de Productos:*\n`

      const effChange = this.change > 0 ? this.change : 1
      const effCopRate = (this.order.total > 0 && this.order.total_cop > 0)
        ? (this.order.total_cop / this.order.total)
        : (effChange * (this.cop_change || 1))

      this.order.products.forEach((item, idx) => {
        const qty = item.quantity || 1
        const name = item.product?.name ?? 'Producto'

        if (currency === 'cop') {
          let uPrice = parseFloat(item.price_cop) || 0
          if (uPrice <= 0 && item.product?.price_cop) {
            uPrice = parseFloat(item.product.price_cop)
          }
          if (uPrice <= 0) {
            uPrice = Math.round((item.price || 0) * effCopRate)
          }
          const lineTotal = Math.round((uPrice * qty) / 100) * 100
          mensaje += `${idx+1}. ${name} x${qty} - COP ${lineTotal.toLocaleString('es-CO')}\n`
        } else if (currency === 'bs') {
          const uPrice = (item.price || 0) * effChange
          const lineTotal = uPrice * qty
          mensaje += `${idx+1}. ${name} x${qty} - Bs. ${lineTotal.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
        } else { // usd or mixto
          const uPrice = item.price || 0
          const lineTotal = uPrice * qty
          mensaje += `${idx+1}. ${name} x${qty} - $${lineTotal.toFixed(2)}\n`
        }
      })

      if (this.order.discount > 0) {
        if (currency === 'cop') {
          const disc = Math.round(((this.order.discount * effCopRate) / 100)) * 100
          mensaje += `\n*Descuento:* -COP ${disc.toLocaleString('es-CO')}\n`
        } else if (currency === 'bs') {
          const disc = this.order.discount * effChange
          mensaje += `\n*Descuento:* -Bs. ${disc.toFixed(2)}\n`
        } else {
          mensaje += `\n*Descuento:* -$${Number(this.order.discount).toFixed(2)}\n`
        }
      }

      mensaje += `\n*TOTAL:*\n`
      if (currency === 'cop') {
        const tot = this.order.total_cop > 0 ? Number(this.order.total_cop) : Math.round((this.order.total * effCopRate) / 100) * 100
        mensaje += `🪙 COP ${tot.toLocaleString('es-CO')}\n`
        mensaje += `_(Equiv. $${Number(this.order.total).toFixed(2)} USD)_\n`
      } else if (currency === 'bs') {
        const tot = this.order.total_bs > 0 ? Number(this.order.total_bs) : (this.order.total * effChange)
        mensaje += `🏦 Bs. ${tot.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
        mensaje += `_(Equiv. $${Number(this.order.total).toFixed(2)} USD)_\n`
      } else if (currency === 'mixto') {
        const totCop = this.order.total_cop > 0 ? Number(this.order.total_cop) : Math.round((this.order.total * effCopRate) / 100) * 100
        const totBs = this.order.total_bs > 0 ? Number(this.order.total_bs) : (this.order.total * effChange)
        mensaje += `💵 $${Number(this.order.total).toFixed(2)} USD\n`
        mensaje += `🪙 Ref. COP ${totCop.toLocaleString('es-CO')}\n`
        mensaje += `🏦 Ref. Bs. ${totBs.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
      } else {
        mensaje += `💵 $${Number(this.order.total).toFixed(2)} USD\n`
      }

      if (this.order.pays && this.order.pays.length > 0) {
        mensaje += `\n*Forma(s) de Pago:*\n`
        this.order.pays.forEach(p => {
          let refText = p.reference ? ` (Ref: ${p.reference})` : ''
          const details = this.getPayDetails(p)

          if (details.type === 'cop') {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: COP ${details.amtCop.toLocaleString('es-CO')}${tasaText} -> Equiv. $${details.amtUsd.toFixed(2)}${refText}\n`
          } else if (details.type === 'bs') {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: Bs. ${details.amtBs.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}${tasaText} -> Equiv. $${details.amtUsd.toFixed(2)}${refText}\n`
          } else {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: $${details.amtUsd.toFixed(2)} USD${tasaText}${refText}\n`
          }
        })
      }

      if (this.vueltoInfo && this.vueltoInfo.hasVuelto) {
        mensaje += `\n*Vuelto Entregado al Cliente:*\n`
        if (this.vueltoInfo.cop > 0) mensaje += `• Pesos: COP ${Number(this.vueltoInfo.cop).toLocaleString('es-CO')}\n`
        if (this.vueltoInfo.usd > 0) mensaje += `• Dólares: $${Number(this.vueltoInfo.usd).toFixed(2)} USD\n`
        if (this.vueltoInfo.bs > 0) mensaje += `• Bolívares: Bs. ${Number(this.vueltoInfo.bs).toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
      }

      mensaje += `\n¡Gracias por su compra!`
      return mensaje
    },
    cancelOrder() {
      if (confirm('Estas seguro que quieres anular esta orden?')) {
        window.location.href = `/orders/${this.order.id}/cancel`
      }
    },
    print(exchange) {
      window.open(`/orders/${this.order.id}/report-thermical/${exchange}`, `Venta #${this.order.number_order}`, 'width=400,height=500', 'toolbar=no')
    },
    addPay() {
      if (this.form.method === 'transferencia' || this.form.method === 'transferencia-usd' || this.form.method === 'zelle') {
        if (!this.form.reference) {
          alert('Debe ingresar una referencia')
          return false
        }
      }
      if (this.order.total < parseInt(this.form.amount) + parseInt(this.order.balance)) {
        return alert('No se puede agregar este pago si el monto es mayor al balance')
      }
      this.form.order_id = this.order.id
      this.form.post('/orders/pay')

      this.cleanPay()
    },
    cleanPay() {
      this.form = this.$inertia.form({
        amount: 0,
        method: '',
        order_id: this.order.id,
        reference: '',
      })
    },
  },
  mounted() {
    window.addEventListener('keyup', (event) => {
      if ((event.key === 'F9' || event.keyCode === 120) && this.order.order_state !== 'request') {
        window.location.href = `/pos`
      }
    });
  }
}
</script>
